/**
 * Reset password page component - shadcn/ui
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { PasswordInput } from '../../components/ui/password-input';
import { LanguageSelector } from '../../components/ui/language-selector';

import { authApi, AuthApiError } from '../../shared/auth';
import { validatePassword, getPasswordHint } from '../../shared/utils/validation';
import { getGenericErrorMessage } from "@/shared/utils/errors";
import { STANDALONE_TITLE } from '../../shared/styles/typography';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('auth');
  const { t: tCommon } = useTranslation('common');
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [tokenEmail, setTokenEmail] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setIsVerifying(false);
        setError(t('resetPassword.noToken'));
        return;
      }
      try {
        const result = await authApi.verifyResetToken({ token });
        setIsTokenValid(true);
        setTokenEmail(result.email || null);
      } catch (err) {
        setError(err instanceof AuthApiError ? err.message : t('resetPassword.invalidToken'));
      } finally {
        setIsVerifying(false);
      }
    };
    verifyToken();
  }, [token, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    const errors: Record<string, string> = {};
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) errors.password = passwordValidation.error!;
    if (!confirmPassword) errors.confirmPassword = t('resetPassword.confirmPasswordRequired');
    else if (password !== confirmPassword) errors.confirmPassword = t('resetPassword.passwordMismatch');

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setIsLoading(true);
    try {
      await authApi.resetPassword({ token, new_password: password });
      setIsSuccess(true);
    } catch (err) {
      if (err instanceof AuthApiError) {
        if (err.details) setFieldErrors(err.details);
        setError(err.message);
      } else {
        setError(getGenericErrorMessage());
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-[400px] w-full">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <p>{t('resetPassword.verifying')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-[400px] w-full">
          <CardContent className="p-8">
            <div className="flex justify-end mb-2">
              <LanguageSelector variant="labeled" />
            </div>
            <div className="flex justify-center mb-2">
              <img src="/panluma-logo.png" alt={tCommon('app.name')} className="h-24" />
            </div>
            <h2 className="text-xl font-semibold text-center mb-4">{t('resetPassword.successTitle')}</h2>
            <Alert className="mb-6 border-green-200 bg-green-50 text-green-800">
              <AlertDescription>{t('resetPassword.successMessage')}</AlertDescription>
            </Alert>
            <Button asChild className="w-full" size="lg">
              <Link to="/login">{t('resetPassword.goToLogin')}</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isTokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-[400px] w-full">
          <CardContent className="p-8">
            <div className="flex justify-end mb-2">
              <LanguageSelector variant="labeled" />
            </div>
            <div className="flex justify-center mb-2">
              <img src="/panluma-logo.png" alt={tCommon('app.name')} className="h-24" />
            </div>
            <h2 className="text-xl font-semibold text-center text-destructive mb-4">{t('resetPassword.invalidTokenTitle')}</h2>
            <Alert variant="destructive" className="mb-6"><AlertDescription>{error || t('resetPassword.invalidToken')}</AlertDescription></Alert>
            <p className="text-sm text-muted-foreground text-center mb-6">{t('resetPassword.requestNewLink')}</p>
            <Button asChild className="w-full mb-4" size="lg"><Link to="/forgot-password">{t('resetPassword.requestNewReset')}</Link></Button>
            <p className="text-sm text-center"><Link to="/login" className="text-primary hover:underline">{t('forgotPassword.backToLogin')}</Link></p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-[400px] w-full">
        <CardContent className="p-8">
          <div className="flex justify-end mb-2">
            <LanguageSelector variant="labeled" />
          </div>
          <h1 className={`${STANDALONE_TITLE} text-center mb-2`}>{tCommon('app.name')}</h1>
          <h2 className="text-xl font-semibold text-center mb-2">{t('resetPassword.title')}</h2>
          {tokenEmail && <p className="text-sm text-muted-foreground text-center mb-6">{t('resetPassword.forEmail', { email: tokenEmail })}</p>}
          {error && <Alert variant="destructive" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}

          <form onSubmit={handleSubmit} noValidate>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">{t('resetPassword.newPassword')}</Label>
                <PasswordInput id="password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={isLoading} autoComplete="new-password" autoFocus className={fieldErrors.password ? 'border-destructive' : ''} />
                <p className="text-xs text-muted-foreground">{fieldErrors.password || getPasswordHint()}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t('resetPassword.confirmPassword')}</Label>
                <PasswordInput id="confirmPassword" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} disabled={isLoading} autoComplete="new-password" className={fieldErrors.confirmPassword ? 'border-destructive' : ''} />
                {fieldErrors.confirmPassword && <p className="text-sm text-destructive">{fieldErrors.confirmPassword}</p>}
              </div>
              <Button type="submit" className="w-full mt-6" size="lg" disabled={isLoading}>
                {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : t('resetPassword.submit')}
              </Button>
              <p className="text-sm text-center"><Link to="/login" className="text-primary hover:underline">{t('forgotPassword.backToLogin')}</Link></p>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
